/*
 * Staff TopBar — page header on /staff. Groups: Home + Admin nav, page title,
 * sound + volume slider, push notification toggle, new-order pulse chip,
 * Set/Change PIN button (staff only), Refresh, Sign out.
 *
 * All state lives in the parent Staff.jsx; this component is a pure view.
 */
import React from "react";
import { Link } from "react-router-dom";
import OfficeAlertsButton from "../../components/OfficeAlertsButton";
import {
  ArrowLeft, Bell, BellRing, ChefHat, KeyRound, RefreshCw, ShieldCheck,
  Volume2, VolumeX
} from "lucide-react";

export default function TopBar({
  user, isAdmin, volume, soundOn, onToggleSound, onSetVolume,
  pushEnabled, onEnablePush, onDisablePush, onManagePin,
  onRefresh, onLogout, newAlert, onClearAlert
}) {
  return (
    <div className="mb-6">
      <div className="flex items-center gap-4 mb-6">
        <Link to="/" className="inline-flex items-center gap-2 text-xs tracking-[0.24em] uppercase text-[var(--mm-cream-muted)] hover:text-[var(--mm-accent)] transition-colors">
          <ArrowLeft size={13} /> Home
        </Link>
        {isAdmin && (
          <Link to="/admin" data-testid="staff-goto-admin" className="inline-flex items-center gap-2 text-xs tracking-[0.24em] uppercase text-[var(--mm-cream-muted)] hover:text-[var(--mm-accent)] transition-colors">
            <ShieldCheck size={13} /> Admin
          </Link>
        )}
      </div>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="section-label mb-2 flex items-center gap-2">
            <ChefHat size={14} /> Back office
          </div>
          <h1 className="font-display italic text-4xl md:text-6xl text-[var(--mm-cream)] leading-[0.95]">
            Service <span className="text-[var(--mm-accent)]">right now.</span>
          </h1>
          <div className="text-[var(--mm-cream-muted)] text-xs tracking-[0.2em] uppercase mt-2">
            {user.name || user.email} · Staff
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {/* Sound + volume slider grouped */}
          <div className="flex items-center gap-2 border border-[var(--mm-line)] px-3 py-2 bg-[var(--mm-bg-alt)]" data-testid="staff-sound-group">
            <button
              onClick={onToggleSound}
              data-testid="staff-sound-toggle"
              title={soundOn ? "Mute the bell" : "Unmute the bell"}
              className="text-[var(--mm-cream)] hover:text-[var(--mm-accent)] transition-colors"
            >
              {soundOn ? <Volume2 size={16} /> : <VolumeX size={16} />}
            </button>
            <input
              type="range"
              min={0}
              max={100}
              value={Math.round((volume || 0) * 100)}
              onChange={(e) => onSetVolume(Number(e.target.value) / 100)}
              aria-label="Bell volume"
              data-testid="staff-volume-slider"
              className="w-24 accent-[var(--mm-accent)] cursor-pointer"
            />
            <span className="text-[0.6rem] tracking-[0.2em] uppercase text-[var(--mm-cream-muted)] w-8 text-right">
              {Math.round((volume || 0) * 100)}
            </span>
          </div>

          {/* Push notifications */}
          {pushEnabled ? (
            <button
              onClick={onDisablePush}
              data-testid="staff-push-disable"
              title="Disable browser notifications"
              className="btn-outline inline-flex items-center gap-2"
            >
              <BellRing size={14} /> Alerts on
            </button>
          ) : (
            <button
              onClick={onEnablePush}
              data-testid="staff-push-enable"
              title="Ring even when this tab is in the background"
              className="btn-outline inline-flex items-center gap-2 opacity-70"
            >
              <Bell size={14} /> Alerts off
            </button>
          )}

          {/* Server push to this device (works even when the app is closed) */}
          <OfficeAlertsButton />

          {newAlert > 0 && (
            <button
              onClick={onClearAlert}
              data-testid="staff-clear-alert"
              className="btn-primary inline-flex items-center gap-2 animate-pulse"
            >
              <BellRing size={14} /> {newAlert} new
            </button>
          )}
          {/* Manage PIN — admins hidden (admins keep password-only) */}
          {!isAdmin && (
            <button
              onClick={onManagePin}
              data-testid="staff-manage-pin"
              className="btn-outline inline-flex items-center gap-2"
              title={user.pin_set ? "Change or clear your PIN" : "Set a 4-digit PIN so you can sign in quickly next shift"}
            >
              <KeyRound size={14} /> {user.pin_set ? "PIN set" : "Set PIN"}
            </button>
          )}
          <button
            onClick={onRefresh}
            data-testid="staff-refresh"
            className="btn-outline inline-flex items-center gap-2"
          >
            <RefreshCw size={14} /> Refresh
          </button>
          <button
            onClick={onLogout}
            data-testid="staff-logout"
            className="text-xs tracking-[0.22em] uppercase text-[var(--mm-cream-muted)] hover:text-[var(--mm-accent)] transition-colors px-3 py-2"
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
